import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import cn from 'classnames';
import emailMask from 'text-mask-addons/dist/emailMask';
import { get } from 'lodash';
import ReactLoading from 'react-loading';
import Collapsible from 'react-collapsible';
import { conformToMask } from 'react-text-mask';

import Validator from 'components/Validator/Validator';
import Header from 'components/Header';
import Heading from 'components/Heading';
import Footer from 'components/Footer';
import Sidebar from 'components/Sidebar';
import ProgressBar from 'components/ProgressBar';
import FormGroup from 'components/Form/FormGroup';
import FormGroupLabel from 'components/Form/FormGroupLabel';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import PopoverCheckbox from 'components/Form/PopoverCheckbox';
import Checkbox from 'components/Form/Checkbox';
import { Button, VerifyButton, ButtonLink } from 'components/Button';
import { Grid, Column } from 'components/Layout';
import Popup from 'components/Popup';
import DownAngle from 'components/Icons/DownAngle';
import { useConfig } from 'config/ConfigContext';

import { nextAction, checkPreviousAction, getTestValuesData } from 'actions/workflow';

import { verifyAddress } from 'utils/verifyAddress';
import { toTitleCase } from 'utils/toTitleCase';
import { unSupportedStates } from 'utils/unSupportedStates';
import { states } from 'utils/states';
import { unmask, numberUnmask } from 'utils/unmask';
import { currencyMask, tbtCurrencyMask } from 'utils/masks';
import { EMPLOYMENT_YEARS, EMPLOYMENT_STATUS } from 'utils/appConstants';

import schema from './schema';
import style from './style.scss';
import { searchAddress } from '../../../utils/verifyAddress';

const Styles = {
  ul: {
    listStyle: 'none',
    backgroundColor: 'white',
    width: '99.5%',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '1px 3px 6px #eee',
    maxHeight: '115px',
    overflowY: 'auto',
    zIndex: 100,
    textOverflow: 'ellipsis',
    display: 'block',
    position: 'absolute',
    top: '44px',
    left: '-7px',
    marginLeft: '10px',
  },
  li: {
    padding: '5px 8px',
    cursor: 'pointer',
    transition: '0.3s',
    lineHeight: '18px',
    borderTop: '0.5px solid #ddd',
    marginBottom: '5px',
  },
  loader: {
    fontSize: '17px',
    position: 'absolute',
    top: '14px',
    right: '134px',
    animation: 'spin .7s infinite linear',
  },
  wrapper: {
    position: 'relative',
  },
};

const Application = ({ className = '', validator, ipAddress }) => {
  const appConfig = useConfig();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const doneTypingInterval = 1000;
  const { values, errors, isValid }= validator;
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isAddressPopupVisible, setIsAddressPopupVisible] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [popupType, setPopupType] = useState(null);
  const [grossMonthlyIncomeDescription, setGrossMonthlyIncomeDescription] = useState(null);
  const [isVerifyButtonClicked, setIsVerifyButtonClicked] = useState(false);
  const [isCheckinProgress, setIsCheckinProgress] = useState(!!key);
  const [hasError, setHasError] = useState(false);
  const [errorMessages, setErrorMessages] = useState(null);
  const [selectedTestUser, setSelectedTestUser] = useState(null);
  const [testSubjectsJson, setTestSubjectsJson] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const form = useRef(null);
  const dropdownRef = useRef(null);
  const fieldRef = useRef(null);
  const applicationId = useSelector((state) => get(state, 'workflow.attributes.entityId') || '');
  const data = useSelector((state) => get(state, 'workflow.data.data'));

  const employmentStatus = get(values, 'employmentStatus');
  const isEmploymentInfoRequired = !(employmentStatus === 'retired' || employmentStatus === 'unemployed' || employmentStatus === 'student');

  useEffect(() => {
    if (!isEmploymentInfoRequired) {
      const formData = {
        ...values,
        occupation: '',
        employmentYears: '',
        employerName: '',
        employerPhone: '',
      };
      validator.setValues(formData);
    }
  }, [isEmploymentInfoRequired]);

  const verifySmartyStreet = ({ address, city, state, zipcode }, formValues) => {
    const { setValues } = validator;

    setIsAddressLoading(true);
    setIsVerifyButtonClicked(true);

    verifyAddress({ zipcode, address, city, state }).then((response) => {
      if (response) {
        if (get(response, 'metadata.recordType') === 'P') {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setIsVerified(false);
          setPopupType(3);
          setIsFirstLoad(false);
        } else {
          const updatedValues = formValues || values;
          const addressData = {
            ...updatedValues,
            address: response.deliveryLine1,
            city: get(response, 'components.cityName'),
            state: get(response, 'components.state'),
            zipcode: `${response.deliveryPointBarcode}`.slice(0, 5),
          };
          setIsVerified(true);
          setIsAddressLoading(false);
          setIsFirstLoad(false);
          setValues(addressData);
        }
      } else {
        setIsAddressPopupVisible(true);
        setIsAddressLoading(false);
        setIsVerified(false);
        setPopupType(2);
        setIsFirstLoad(false);
      }
    }).catch((error) => {
      console.log('error', error);
      setIsAddressLoading(false);
      setIsVerified(false);
      setPopupType(1);
      setIsFirstLoad(false);
    });
  };

  const loadHandleSetValues = () => {
    dispatch(getTestValuesData({
      url: '/lookup/set-values-fsl-application',
      success: (res) => {
        const testList = {
          default: res.default.attributes,
          applications: res.applications.attributes.values,
        };
        setTestSubjectsJson(testList);
      },
      fail: (err) => {
        console.log('Error', err);
      },
    }));
  };

  useEffect(() => {
    loadHandleSetValues();
  }, []);

  useEffect(() => {
    if (testSubjectsJson) {
      const { setValues } = validator;

      if (key) {
        if (data) {
          const initialFormData = {
            firstName: get(data, 'firstName') || '',
            lastName: get(data, 'lastName') || '',
            email: get(data, 'email') || '',
            requestedLoanAmount: `${get(data, 'requestedLoanAmount') || ''}`,
            customerDedicatedAccountBalance: `${get(data, 'customerDedicatedAccountBalance') || ''}`,
            monthlySavingsAmount: `${get(data, 'monthlySavingsAmount') || ''}`,
            address: get(data, 'address') || '',
            city: get(data, 'city') || '',
            zipcode: get(data, 'zipCode') || '',
            state: get(data, 'state') || '',
            dateOfBirth: (get(data, 'dateOfBirth') && format(get(data, 'dateOfBirth'), 'MM/dd/yyyy')) || '',
            ssn: (
              get(data, 'ssn') &&
                conformToMask(
                  get(data, 'ssn'),
                  [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
                ).conformedValue
            ) || '',
            grossMonthlyIncome: (get(data, 'grossMonthlyIncome') && get(data, 'grossMonthlyIncome').toString()) || '',
            employerName: get(data, 'employerName') || '',
            employmentYears: get(data, 'employmentYears') || '',
            employmentStatus: get(data, 'employmentStatus') || '',
            occupation: get(data, 'occupation') || '',
            housingExpense: get(data, 'data.data.housingExpense') || '',
            employerPhone: (
              get(data, 'employerPhone') &&
                conformToMask(
                  get(data, 'employerPhone'),
                  ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
                ).conformedValue
            ) || '',
            merchantId: 100150,
            isPrimary: true,
            type: 'Physical',
            mobilePhone: (
              get(data, 'phoneNumber') &&
                conformToMask(
                  get(data, 'phoneNumber'),
                  ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
                ).conformedValue
            ) || '',
          };
          setValues(initialFormData);
          const testUser = appConfig.env !== 'production' && testSubjectsJson && testSubjectsJson.applications && testSubjectsJson.applications.find(i => i.firstName === get(data, 'firstName') && i.lastName === get(data, 'lastName'));
          if (testUser) {
            setSelectedTestUser(testUser);
          } else if (initialFormData.address) {
            setTimeout(() => {
              verifySmartyStreet({
                address: get(initialFormData, 'address'),
                city: get(initialFormData, 'city'),
                state: get(initialFormData, 'state'),
                zipcode: get(initialFormData, 'zipcode'),
              }, initialFormData);
            }, 50);
          }
          setIsCheckinProgress(false);
          return;
        }

        const token = searchParams.get('token');
        dispatch(checkPreviousAction({
          data: {
            token: decodeURIComponent(token) || '',
          },
          url: `/workflows/fsl/${key}/checkin`,
          success: (response) => {
            const routeUrl = response.data && response.data.url;
            navigate(routeUrl);
            const initialFormData = {
              firstName: response.data.data.firstName,
              lastName: response.data.data.lastName,
              email: response.data.data.email,
              requestedLoanAmount: `${response.data.data.requestedLoanAmount}`,
              customerDedicatedAccountBalance: `${response.data.data.customerDedicatedAccountBalance}`,
              monthlySavingsAmount: `${response.data.data.monthlySavingsAmount}`,
              merchantId: 100150,
              isPrimary: true,
              type: 'Physical',
              mobilePhone: `${response.data.data.phoneNumber}`,
              address: response.data.data.address,
              city: response.data.data.city,
              zipcode: response.data.data.zipCode,
              state: response.data.data.state,
              dateOfBirth: (get(response, 'data.data.dateOfBirth') && format(get(response, 'data.data.dateOfBirth'), 'MM/dd/yyyy')) || '',
              ssn: (
                get(response, 'data.data.ssn') &&
                  conformToMask(
                    get(response, 'data.data.ssn'),
                    [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
                  ).conformedValue
              ) || '',
              grossMonthlyIncome: (get(response, 'data.data.grossMonthlyIncome') && get(response, 'data.data.grossMonthlyIncome').toString()) || '',
              employerName: get(response, 'data.data.employerName') || '',
              employmentYears: get(response, 'data.data.employmentYears') || '',
              employmentStatus: get(response, 'data.data.employmentStatus') || '',
              occupation: get(response, 'data.data.occupation') || '',
              housingExpense: get(response, 'data.data.housingExpense') || '',
              employerPhone: (
                get(response, 'data.data.employerPhone') &&
                  conformToMask(
                    get(response, 'data.data.employerPhone'),
                    ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
                  ).conformedValue
              ) || '',
            };
            setValues(initialFormData);
            const testUser = appConfig.env !== 'production' && testSubjectsJson && testSubjectsJson.applications && testSubjectsJson.applications.find(i => i.firstName === get(response, 'data.data.firstName') && i.lastName === get(response, 'data.data.lastName'));
            if (testUser) {
              setSelectedTestUser(testUser);
            } else if (initialFormData.address) {
              setTimeout(() => {
                verifySmartyStreet({
                  address: get(initialFormData, 'address'),
                  city: get(initialFormData, 'city'),
                  state: get(initialFormData, 'state'),
                  zipcode: get(initialFormData, 'zipcode'),
                }, initialFormData);
              }, 50);
            }
            setIsCheckinProgress(false);
          },
          fail: (error) => {
            console.log(error);
            setIsCheckinProgress(false);
          },
        }));
      }
    }
  }, [testSubjectsJson]);

  useEffect(() => {
    if (appConfig.env !== 'production' && selectedTestUser && selectedTestUser.value) {
      const { setValues } = validator;
      const formData = {
        ...testSubjectsJson.default,
        ...selectedTestUser.value,
      };
      const updatedData = { ...values, ...formData };
      setValues(updatedData);
      setTimeout(() => {
        verifySmartyStreet({
          address: get(formData, 'address'),
          city: get(formData, 'city'),
          state: get(formData, 'state'),
          zipcode: get(formData, 'zipcode'),
        }, updatedData);
      }, 50);
    }
  }, [selectedTestUser]);


  const onSuggestionClick = (suggestion) => {
    const { setValues } = validator;
    const updatedValues = { ...values };
    updatedValues.address = `${suggestion.streetLine} ${suggestion.secondary || ''}`.trim();
    updatedValues.city = suggestion.city;
    updatedValues.state = suggestion.state;
    updatedValues.zipcode = suggestion.zipcode;

    if (unSupportedStates.indexOf(suggestion.state) !== -1) {
      setIsAddressPopupVisible(true);
      setPopupType(4);
      setValues(updatedValues);
      setShowSuggestions(false);
      return;
    }

    setValues(updatedValues);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  let typingTimer;
  const doneTyping = async () => {
    const { address, city, state, zipcode } = values;
    try {
      const results = await searchAddress({
        address: address || '',
        city: city || '',
        state: state || '',
        zipcode: zipcode || '',
      });

      setSuggestions(results);
      if (results.length > 0) {
        setShowSuggestions(true);
      }
    } catch (e) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setIsSuggestionLoading(false);
  };

  const onKeyUpHandler = () => {
    clearTimeout(typingTimer);
    setIsSuggestionLoading(true);
    typingTimer = setTimeout(doneTyping, doneTypingInterval);
  };

  const onKeyDownHandler = () => {
    clearTimeout(typingTimer);
  };

  const handleSubmitFrom = (data, e) => {
    e.preventDefault();
    const { validate } = validator;
    setIsSubmitted(true);
    const isAddressValid = appConfig.smartyStreetEnforce ? isVerified : true;
    if (validate(schema).isValid && isAddressValid) {
      setIsLoading(true);

      const formData = {
        ...data,
        consentToReceiveMessages: true,
        requestedLoanAmount: unmask(data.requestedLoanAmount.toString()),
        customerDedicatedAccountBalance: unmask(data.customerDedicatedAccountBalance.toString()),
        monthlySavingsAmount: unmask(data.monthlySavingsAmount.toString()),
        grossMonthlyIncome: unmask(data.grossMonthlyIncome.toString()),
        housingExpense: unmask(data.housingExpense.toString()),
        ssn: numberUnmask(data.ssn.toString()),
        employerPhone: data.employerPhone ? numberUnmask(data.employerPhone.toString()) : '',
        mobilePhone: numberUnmask(data.mobilePhone.toString()),
        sourceIP: ipAddress,
      };

      dispatch(nextAction({
        data: formData,
        url: `/workflows/fsl/${key}/next`,
        success: (response) => {
          setIsLoading(false);
          navigate(`${response.data.url}`);
        },
        fail: (error) => {
          if (error && error.status === 400) {
            setHasError(true);
            setIsSubmitted(false);
            setIsLoading(false);
            const errorMsg = (error.data.failure.errors && error.data.failure.errors.length > 0) ? error.data.failure.errors.map(err => err.message) : null;
            setErrorMessages(errorMsg);
          } else {
            navigate(`/fsl/declined?key=${key}`, {
              state: {
                data: error.data,
              },
            });
          }
        },
      }));
    } else {
      console.log('api error', errors, !isVerified && 'address is not verified');
    }
  };

  const initializeAddressState = () => {
    setIsVerified(false);
    setIsSubmitted(false);
    setIsVerifyButtonClicked(false);
    setIsAddressPopupVisible(false);
  };

  const handleInputChange = (event) => {
    const { onChangeHandler } = validator;
    switch (event.target.name) {
      case 'state':
        initializeAddressState();
        onChangeHandler(event.target.name, event.target.value);
        break;
      case 'zipcode':
        initializeAddressState();
        onChangeHandler(event.target.name, event.target.value);
        break;
      case 'address':
      case 'city':
        initializeAddressState();
        onChangeHandler(event.target.name, (event.target.value).replace(/[^a-zA-Z0-9-.,#/;:'&%@ ']/g, ''));
        break;
      case 'businessType':
        onChangeHandler(event.target.name, event.target.value);
        if (event.target.value === 'Funeral') {
          onChangeHandler('productsOrServices', event.target.value);
        } else {
          onChangeHandler('productsOrServices', '');
        }
        break;
      case 'grossMonthlyIncome':
        onChangeHandler(event.target.name, event.target.value);
        setGrossMonthlyIncomeDescription(null);
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  };

  const handleBlur = (event) => {
    event.preventDefault();

    const { onChangeHandler } = validator;

    switch (event.target.name) {
      case 'state':
        if (unSupportedStates.indexOf(event.target.value) !== -1) {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setPopupType(4);
        } else if (isFirstLoad && get(values, 'city') && get(values, 'address')) {
          verifySmartyStreet({
            zipcode: get(values, 'zipcode'),
            address: get(values, 'address'),
            city: get(values, 'city'),
            state: event.target.value,
          });
        }
        break;
      case 'zipcode':
        if (isFirstLoad && get(values, 'city') && get(values, 'state') && get(values, 'address')) {
          verifySmartyStreet({
            zipcode: event.target.value,
            address: get(values, 'address'),
            city: get(values, 'city'),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'address':
        if (isFirstLoad && get(values, 'city') && get(values, 'state')) {
          verifySmartyStreet({
            zipcode: get(values, 'zipcode'),
            address: toTitleCase(event.target.value),
            city: get(values, 'city'),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'city':
        if (isFirstLoad && get(values, 'address') && get(values, 'state')) {
          verifySmartyStreet({
            zipcode: get(values, 'zipcode'),
            address: get(values, 'address'),
            city: toTitleCase(event.target.value),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'grossMonthlyIncome':
        if (Number(unmask(event.target.value)) >= 41666.66) {
          setGrossMonthlyIncomeDescription('The amount should not exceed $41,600.');
        }
        break;
      default:
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
    }
  };

  const handleCheckboxChange = (name, value) => {
    const { onChangeHandler } = validator;
    onChangeHandler(name, value);
  };

  const handleAbort = () => {
    setIsAddressPopupVisible(false);
  };

  const handleOverride = () => {
    setIsAddressPopupVisible(false);
  };

  const handleVerifyAddressClick = (e) => {
    e.preventDefault();

    if (!get(values, 'address')) {
      setIsAddressPopupVisible(true);
      setPopupType(1);
      setIsVerifyButtonClicked(true);
    } else {
      verifySmartyStreet({
        address: get(values, 'address'),
        city: get(values, 'city'),
        state: get(values, 'state'),
        zipcode: get(values, 'zipcode'),
      });
    }
  };

  const handleUndo = (e) => {
    e.preventDefault();

    const { setValues } = validator;
    const addressData = {
      ...values,
      address: '',
      city: '',
      state: '',
      zipcode: '',
    };
    setIsVerified(false);
    setValues(addressData);
  };

  const togglePopup = (e) => {
    e.preventDefault();
    setIsPopupVisible(!isPopupVisible);
  };

  if (!errors.address && isSubmitted && !isVerified && appConfig.smartyStreetEnforce) {
    errors.address = isVerifyButtonClicked ? 'Address is not valid, please enter valid address' : 'Please click verify Address';
  }

  if (form.current && !isValid && isSubmitted) {
    const errs = Object.keys(errors);
    form.current[errs[0]].focus();
    setIsSubmitted(false);
  }

  const handleClickOutside = (event) => {
    if (dropdownRef.current && fieldRef.current) {
      if (
        !dropdownRef.current.contains(event.target)
        && !fieldRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    }
  };
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <form className={cn(style.App, className)} onSubmit={handleSubmitFrom.bind(null, values)} ref={form}>
      <Header />
      <section className="section">
        <div className={cn('grid-container fluid borrowers-apply', style.container)}>
          <div className="grid-container">
            {
              !isCheckinProgress ?
                <Grid className="grid-margin-x max-limited">
                  {applicationId ? (
                    <div className="cell small-12 medium-12">
                      <p className="p-large"><b>Application ID:</b> {applicationId}</p>
                    </div>
                  ) : null}
                  <Heading heading="Check My Rate" />
                  <Column className="small-12 large-8 main-column">
                    <ProgressBar />
                    <FormGroup className={style.totalDebtAmount}>
                      <Collapsible
                        trigger={
                          <div className={style.collapsible}>
                            <FormGroupLabel className="abc" value="Total Loan Amount" />
                            <DownAngle width={12} className={cn(style.icon, 'fa-angle')} />
                          </div>
                        }
                      >
                        <Grid className={cn('grid-margin-x', style.marginTop1)}>
                          <Column className="small-12 medium-12">
                            <Input
                              label="Total Loan Amount"
                              name="requestedLoanAmount"
                              value={`${values.requestedLoanAmount}`}
                              onChange={handleInputChange}
                              isRequired
                              placeHolder="($1,000.00 - $43,000.00)"
                              hasError={!!errors.requestedLoanAmount}
                              isMasked={tbtCurrencyMask}
                              isBadgeVisible={false}
                              notification="This is an estimated amount"
                              notificationClass="notificationFromTop"
                              readOnly
                            />
                          </Column>
                          <Column className="small-6 medium-6">
                            <Input
                              label="Customer Dedicated Account Balance"
                              name="customerDedicatedAccountBalance"
                              value={`${values.customerDedicatedAccountBalance}`}
                              onChange={handleInputChange}
                              isRequired
                              hasError={!!errors.customerDedicatedAccountBalance}
                              isMasked={currencyMask}
                              isBadgeVisible={false}
                              readOnly
                            />
                          </Column>
                          <Column className="small-6 medium-6">
                            <Input
                              label="Monthly Savings Amount"
                              name="monthlySavingsAmount"
                              value={`${values.monthlySavingsAmount}`}
                              onChange={handleInputChange}
                              isRequired
                              hasError={!!errors.monthlySavingsAmount}
                              isMasked={currencyMask}
                              isBadgeVisible={false}
                              readOnly
                            />
                          </Column>
                        </Grid>
                      </Collapsible>
                    </FormGroup>
                    <FormGroup>
                      <FormGroupLabel value="Personal Information" />
                      <p>Please provide the following personal information</p>
                      <Grid className="grid-margin-x">
                        <Column className="small-12 medium-6">
                          <Input
                            label="First Name"
                            name="firstName"
                            value={values.firstName}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.firstName}
                            isBadgeVisible={false}
                            readOnly
                          />
                        </Column>
                        <Column className="small-12 medium-6">
                          <Input
                            label="Last Name"
                            name="lastName"
                            value={values.lastName}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.lastName}
                            isBadgeVisible={false}
                            readOnly
                          />
                        </Column>
                        <Column className={cn('small-12 medium-12', style.positionRelative)}>
                          <Input
                            label="Street Address"
                            name="address"
                            ref={fieldRef}
                            className={style.streetAddress}
                            value={values.address}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.address}
                            onBlur={handleBlur}
                            errorMessage={errors.address}
                            onKeyUp={() => onKeyUpHandler()}
                            onKeyDown={e => onKeyDownHandler(e)}
                            onFocusHandler={() => suggestions && suggestions.length > 0 && setShowSuggestions(true)}
                          />
                          {isSuggestionLoading ? <span style={Styles.loader} className="glyphicon glyphicon-refresh" /> : null}
                          {showSuggestions && suggestions && suggestions.length > 0 ? (
                            <ul
                              style={Styles.ul}
                              ref={dropdownRef}
                            >
                              {suggestions.map(suggestion => (
                                <li style={Styles.li} onClick={() => onSuggestionClick(suggestion)}>
                                  {`${suggestion.streetLine}
                                ${suggestion.secondary ? ` ${suggestion.secondary},` : ''}
                                ${suggestion.city},
                                ${suggestion.state},
                                ${suggestion.zipcode}
                              `}
                                </li>
                      ))}
                            </ul>
                  ) : null}
                          <VerifyButton
                            onClick={isVerified ? handleUndo : handleVerifyAddressClick}
                            isVerified={isVerified}
                            isLoading={isAddressLoading}
                            ref={fieldRef}
                          />
                          { isAddressPopupVisible &&
                            <Popup
                              handleOverride={handleOverride}
                              handleAbort={handleAbort}
                              type={popupType}
                              data={{
                                address: get(values, 'address'),
                                city: get(values, 'city'),
                                state: get(values, 'state'),
                                zipcode: get(values, 'zipcode'),
                              }}
                            />
                          }
                        </Column>
                        <Column className="small-12 medium-6">
                          <Input
                            label="City"
                            name="city"
                            value={values.city}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.city}
                            onBlur={handleBlur}
                            errorMessage={errors.city}
                          />
                        </Column>
                        <Column className="small-12 medium-6">
                          <Grid className="grid-margin-x">
                            <Column className="small-8">
                              <Select
                                name="state"
                                data={states}
                                value={values.state}
                                onBlur={handleBlur}
                                onChange={handleInputChange}
                                label="State"
                                isRequired
                                hasError={!!errors.state}
                                errorMessage={errors.state}
                              />
                            </Column>
                            <Column className={cn('cell small-4', style.positionRelative)}>
                              <Input
                                label="Zip"
                                isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                                name="zipcode"
                                value={values.zipcode}
                                onChange={handleInputChange}
                                isRequired
                                hasError={!!errors.zipcode}
                                errorMessage={errors.zipcode}
                                onBlur={handleBlur}
                              />
                            </Column>
                          </Grid>
                        </Column>
                        <Column className="small-12 medium-6">
                          <Input
                            label="Date of Birth"
                            name="dateOfBirth"
                            isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                            placeHolder="__/__/____"
                            value={values.dateOfBirth}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.dateOfBirth}
                            errorMessage={errors.dateOfBirth}
                          />
                        </Column>
                        <Column className="small-12 medium-6">
                          <Input
                            label="Social Security #"
                            name="ssn"
                            isMasked={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                            placeHolder="___-__-____"
                            value={values.ssn}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.ssn}
                            errorMessage={errors.ssn}
                          />
                        </Column>
                      </Grid>
                    </FormGroup>
                    <FormGroup>
                      <FormGroupLabel value="Contact Information" />
                      <p>Loan and application status will be sent to the following.</p>
                      <Grid className="grid-margin-x">
                        <Column className="small-6 medium-6">
                          <Input
                            label="Email Address"
                            name="email"
                            isMasked={emailMask}
                            placeHolder="Email address"
                            value={values.email}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.email}
                            isBadgeVisible={false}
                            readOnly
                          />
                        </Column>
                        <Column className="small-6 medium-6">
                          <Input
                            label="Mobile Phone"
                            name="mobilePhone"
                            placeHolder="(___) ___-____"
                            isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                            value={values.mobilePhone}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.mobilePhone}
                            errorMessage={errors.mobilePhone}
                          />
                        </Column>
                      </Grid>
                      <p className="d-flex mb-0">
                        I consent to receive calls and texts for any purpose, including marketing, under these&nbsp;
                        <ButtonLink
                          onClick={togglePopup}
                          className={style.viewDetailCheckbox}
                        >
                          terms
                        </ButtonLink>.
                      </p>
                    </FormGroup>

                    <FormGroup>
                      <FormGroupLabel value="Financials & Employment" />
                      <p>Your total verifiable gross income might include your salary, retirement income or other sources of income. You don&apos;t have to include alimony, child support, or separate maintenance income unless you want it considered as a basis for repayment.</p>
                      <p><strong>All stated income and/or employment details will <u>require verification</u> prior to final loan approval. Please note that this process may result in changes to the terms and conditions of your loan.</strong></p>
                      <Grid className="grid-margin-x">
                        <Column>
                          <p className={style.description}>
                            Note: $1,458.34 Minimum Gross Income Required
                            <br />
                            (Gross Income is before any deductions)
                          </p>
                        </Column>
                        <Column />
                        <Column className="small-6 medium-6">
                          <Input
                            label="Gross MONTHLY Income"
                            name="grossMonthlyIncome"
                            value={`${values.grossMonthlyIncome}`}
                            onChange={handleInputChange}
                            placeHolder="($1.00 - $41,600.00)"
                            hasError={!!errors.grossMonthlyIncome}
                            isMasked={currencyMask}
                            errorMessage={errors.grossMonthlyIncome || grossMonthlyIncomeDescription}
                            isRequired
                            onBlur={handleBlur}
                          />
                        </Column>
                        <Column className="small-6 medium-6">
                          <Select
                            name="employmentStatus"
                            data={Object.keys(EMPLOYMENT_STATUS).map(value => ({ value, title: EMPLOYMENT_STATUS[value] }))}
                            value={values.employmentStatus}
                            onChange={handleInputChange}
                            label="Employment Status"
                            isRequired
                            hasError={!!errors.employmentStatus}
                            errorMessage={errors.employmentStatus}
                          />
                        </Column>
                        <Column className="small-6 medium-6">
                          <Input
                            label="Employer Name"
                            name="employerName"
                            value={values.employerName}
                            onChange={handleInputChange}
                            isRequired={isEmploymentInfoRequired}
                            isDisabled={!isEmploymentInfoRequired}
                            hasError={!!errors.employerName}
                            errorMessage={errors.employerName}
                          />
                        </Column>
                        <Column className="small-6 medium-6">
                          <Select
                            name="employmentYears"
                            data={Object.keys(EMPLOYMENT_YEARS).map(key => ({ title: EMPLOYMENT_YEARS[key], value: key }))}
                            value={values.employmentYears}
                            onChange={handleInputChange}
                            label="Years with Current Employer"
                            isRequired={isEmploymentInfoRequired}
                            isDisabled={!isEmploymentInfoRequired}
                            hasError={!!errors.employmentYears}
                          />
                        </Column>
                        <Column className="small-6 medium-6">
                          <Input
                            label="Occupation"
                            name="occupation"
                            value={values.occupation}
                            onChange={handleInputChange}
                            isRequired={isEmploymentInfoRequired}
                            isDisabled={!isEmploymentInfoRequired}
                            hasError={!!errors.occupation}
                            errorMessage={errors.occupation}
                          />
                        </Column>
                        <Column className="small-6 medium-6">
                          <Input
                            label="Employer Phone"
                            name="employerPhone"
                            placeHolder={!isEmploymentInfoRequired ? '' : '(___) ___-____'}
                            isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                            value={values.employerPhone}
                            onChange={handleInputChange}
                            isRequired={isEmploymentInfoRequired}
                            isDisabled={!isEmploymentInfoRequired}
                            hasError={!!errors.employerPhone}
                            errorMessage={errors.employerPhone}
                          />
                        </Column>
                        <Column>
                          <p className={style.description}>
                            Note: This should be your monthly
                            <br />
                            portion of Mortgage or Rent
                          </p>
                        </Column>
                        <Column />
                        <Column className="small-6 medium-6">
                          <Input
                            label="Monthly Mortgage/Rent"
                            name="housingExpense"
                            isMasked={tbtCurrencyMask}
                            value={values.housingExpense}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors.housingExpense}
                            errorMessage={errors.housingExpense}
                          />
                        </Column>
                        <Column className="small-12">
                          <Checkbox
                            label={['By checking this box, I hereby authorize LendingUSA to verify my income and employment, which will include contacting my employer if applicable.']}
                            name="consentToVerifyIncomeEmployment"
                            onChange={handleCheckboxChange.bind(null, 'consentToVerifyIncomeEmployment', !values.consentToVerifyIncomeEmployment)}
                            isChecked={values.consentToVerifyIncomeEmployment}
                            value="on"
                            id="consentToVerifyIncomeEmployment"
                            type="checkbox"
                            required
                            errorMessage={errors.consentToVerifyIncomeEmployment}
                          />
                        </Column>
                      </Grid>
                    </FormGroup>
                    <FormGroup>
                      <Grid className="grid-margin-x">
                        <Column className="small-12 checkbox-row">
                          <h6>By checking the box below I agree by electronic signature to the following:</h6>
                        </Column>
                        <Column className="small-12">
                          <PopoverCheckbox
                            name="consentElectronicCommunication"
                            onChange={handleCheckboxChange.bind(null, 'consentElectronicCommunication')}
                            isChecked={values.consentElectronicCommunication}
                            id="consentElectronicCommunication"
                            errorMessage={errors.consentElectronicCommunication}
                            hasError={!!errors.consentElectronicCommunication}
                            label={['Electronic Consent']}
                            title="Electronic Consent"
                            hasScrollCondition={false}
                          >
                            <b>CONSENT FOR ELECTRONIC SIGNATURES, RECORDS, AND DISCLOSURES (&#34;E-Consent&#34;)</b>
                            <br />
                            Please read this information carefully and print a copy and/or retain this information electronically for future reference.
                            <br />
                            <br />
                            <b>Introduction.</b> You are using this <u>&#8220;Portal&#8221;</u> to submit a request for consumer financial services from these <a href="https://lendingusa.com/lending-partners/" target="_blank" rel="noopener noreferrer">lenders</a> (each a <u>&#34;Bank&#34;</u>), LendingUSA, LLC (<u>&#8220;LendingUSA&#8221;</u>) as operator of the Portal, and their assignees, among other services. To provide these services, LendingUSA needs your consent to use and accept electronic signatures, records, and disclosures, as described in this document (the <u>&#34;E-Consent&#34;</u>). This E-Consent notifies you of your rights when receiving disclosures, notices, and information from a Bank or LendingUSA. By clicking links or buttons assenting to our terms (<u>&#34;Links&#34;</u>), you agree that you received, read and understood this E-Consent and that you hereby consent to using electronic signatures, records, and disclosures. You further consent and agree that your use of a key pad, mouse or other device to click Links or otherwise select an item, button, icon or similar act while using the Portal, or in accessing or making any transactions regarding any agreement, acknowledgment, consent, terms, disclosures or conditions, constitutes your signature, acceptance, and agreement as if actually signed by you in writing, and you consent to conduct transactions by using electronic disclosures, electronic records, and contract documents (collectively, <u>&#34;Disclosures&#34;</u>). Further, you agree that no certification authority or other third party verification is necessary to establish the validity of your electronic signature, and that the lack of such certification or third party verification will not affect the enforceability of your signature or any resulting contract between you and a Bank or LendingUSA.
                            <br />
                            <br />
                            <b>Option for Paper or Non-Electronic Records.</b> You may request any Disclosures in paper copy by logging in and printing a paper copy. You may also mail your written request to LendingUSA, at the following address: 15021 Ventura Blvd. #879, Sherman Oaks, CA 91403. Upon request, LendingUSA will provide paper copies at no charge. The Bank or its assignee will retain all Disclosures as applicable law requires.
                            <br />
                            <br />
                            <b>Scope of Consent.</b> This E-Consent applies to all interactions online concerning you, Bank or LendingUSA, and includes those interactions engaged in on any computer, electronic device, mobile device, including phones, smart-phones, fax machines, and tablets. Under this E-Consent, Bank or LendingUSA will process your information and interact during all online interactions with you electronically. The Disclosures you will receive electronically include federal disclosures, such as the Truth-in-Lending disclosures, Privacy Notices, and notices of our credit decisions, as well as state-specific notices. LendingUSA will also send you notices electronically related to our interactions and transactions. This E-Consent informs you of your rights when receiving these Disclosures electronically.
                            <br /><br />
                            <b>Consenting to Do Business Electronically.</b> Before you decide to do business electronically with Bank or LendingUSA, you should consider whether you have the required hardware and software capabilities described below.
                            <br /><br />
                            <b>Hardware and Software Requirements.</b> To access and retain the Disclosures electronically, you will need to use a computer or device capable of accessing the Internet and an Internet Browser software program that supports at least 256-bit encryption, such as the evergreen Safari, Chrome, Firefox or Edge. To read some documents, you may need a PDF file reader like Adobe® Acrobat Reader, Xpdf® or Foxit®. If the foregoing requirements change while you are maintaining an active relationship with Bank, and the change creates a material risk that you may not be able to receive Disclosures electronically, Bank will notify you of these changes. You may need a printer or a long-term storage device, such as your computer&apos;s disk drive, if you wish to retain a copy of the Disclosures for future reference. You are solely responsible for meeting the hardware and software requirements described in this E-Consent and LendingUSA and Bank make no warranties and will have no responsibility for the operation of your hardware or software, or the interoperability of such hardware and software with the Disclosures or the Portal. You may send us your written questions regarding the hardware and software requirements to:
                            <br /><br />
                            <b>Email</b><br />
                            <a href="mailto:customercare@lendingusa.com">CustomerCare@LendingUSA.com</a>
                            <br /><br />
                            <b>Call</b><br />
                            Our regular business hours are 7:00am to 4:00pm PST<br />
                            <a href="tel:+18006978743">800-697-8743</a>
                            <br /><br />
                            <b>Address for Regular Mail</b><br />
                            15021 Ventura Blvd., #879<br />
                            Sherman Oaks, CA 91403
                            <br /><br />
                            <b>Withdrawing Consent.</b> You are free to withdraw this E-Consent at any time and at no charge. If you withdraw this E-Consent before receiving consumer financial services, this will prevent you from obtaining consumer financial services from LendingUSA. If at any time you wish to withdraw this E-Consent, you can send LendingUSA your written request by e-mail to <a href="mailto:customercare@lendingusa.com">CustomerCare@LendingUSA.com</a> with the details of such request. If you decide to withdraw this E-Consent, the withdrawal will not affect the legal effectiveness, validity, and enforceability of any prior electronic Disclosures.
                            <br /><br />
                            <b>Change to Your Contact Information.</b> You agree to keep LendingUSA informed of any change in your electronic address or mailing address. You may update such information by calling or emailing LendingUSA to provide the updated information. You may also send LendingUSA your written update by mail to our address above. LendingUSA will not be responsible for any delays or failures in the performance of its obligations under this E-Consent or any other terms to the extent caused by your failure to maintain up-to-date contact information with LendingUSA.
                            <br /><br />
                            <b>YOUR ABILITY TO ACCESS DISCLOSURES.</b> BY CLICKING LINKS, YOU ASSENT TO LENDINGUSA&#8217;S TERMS. YOU ACKNOWLEDGE AND REPRESENT THAT YOU CAN ACCESS THE DISCLOSURES IN THE DESIGNATED FORMATS DESCRIBED ABOVE.
                            <br /><br />
                            <b>CONSENT.</b> YOU ACKNOWLEDGE YOU HAVE READ AND UNDERSTAND THE CONTENTS OF THIS E-CONSENT, INCLUDING THE INFORMATION ABOUT ELECTRONIC SIGNATURES, RECORDS, DISCLOSURES, AND DOING BUSINESS ELECTRONICALLY. YOU HEREBY CONSENT TO USING ELECTRONIC SIGNATURES, HAVING ALL DISCLOSURES PROVIDED OR MADE AVAILABLE TO YOU IN ELECTRONIC FORM, AND TO DOING BUSINESS WITH US ELECTRONICALLY. YOU ACKNOWLEDGE THAT YOU MAY REQUEST A PAPER COPY OF THE ELECTRONIC RECORDS AND DISCLOSURES, WHICH WE WILL PROVIDE TO YOU AT NO CHARGE. IF YOU REFRAIN FROM PROCEEDING THEN YOU NEITHER WISH TO USE ELECTRONIC SIGNATURES NOR CONDUCT THIS TRANSACTION ELECTRONICALLY. YOU ALSO ACKNOWLEDGE THAT YOUR CONSENT TO ELECTRONIC DISCLOSURES IS REQUIRED TO RECEIVE CONSUMER FINANCIAL SERVICES FROM LENDINGUSA OVER THE INTERNET. PRINT AND RETAIN A HARD COPY OR ELECTRONIC COPY.
                            <br /><br />
                            <b>ELECTRONIC CONSENT AND ACKNOWLEDGMENT.</b> BY CLICKING LINKS, YOU CERTIFY THAT:
                            <ol>
                              <li>Your computer hardware and software meet the requirements needed to access and retain the Disclosures electronically.</li>
                              <li>You consent to receive the Disclosures electronically.</li>
                              <li>You consent to sign any agreements and related documents among or between yourself the Bank, and LendingUSA electronically.</li>
                              <li>You understand and agree that you will be bound to the same terms and conditions if you sign the Disclosures electronically as if you signed paper Disclosures.</li>
                            </ol>
                          </PopoverCheckbox>
                        </Column>
                        <Column className="small-12">
                          <PopoverCheckbox
                            name="consentToTermsAndConditions"
                            onChange={handleCheckboxChange.bind(null, 'consentToTermsAndConditions')}
                            isChecked={values.consentToTermsAndConditions}
                            id="consentToTermsAndConditions"
                            errorMessage={errors.consentToTermsAndConditions}
                            hasError={!!errors.consentToTermsAndConditions}
                            label={['I consent to the following terms, conditions, acknowledgements and disclosures.']}
                            title="I consent to the following terms, conditions, acknowledgements and disclosures."
                            hasScrollCondition={false}
                          >
                            By checking this box you authorize LendingUSA, LLC, (“LendingUSA”), Cross River Bank (“Lender”) and their affiliated third parties (“Third Parties”) to forward your loan application (“Application”), including your non-public personal information, to third parties to verify the information contained in your Application, including, but not limited to the following: (1) contacting your employer(s) to verify employment and income; (2) contacting your Merchant Provider (if applicable) to verify any of the following information: (i) the type of procedure(s), service(s) and/or product(s) to be provided; (ii) the date of service; (iii) cost of the goods and/or services to be provided. You acknowledge that you have reviewed and agree to our Privacy Policy which can be found at <a href="https://www.lendingusa.com/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.lendingusa.com/privacy-policy</a> and you have reviewed and agree to our Terms of Use which can be found at <a href="https://www.lendingusa.com/terms-of-use" target="_blank" rel="noopener noreferrer">https://www.lendingusa.com/terms-of-use</a>.
                            <br /><br />
                            You understand that APRs will vary depending upon credit ratings and/or the payment terms that are approved. Credit approvals are valid for a limited time only. You understand that checking your rate and reviewing loan offers on LendingUSA&apos;s Text-to-Apply mobile platform, merchant portal, or any other LendingUSA affiliated online platform results in a soft credit inquiry which does not affect your credit score. However, if you receive a preapproval (“Offer“) from LendingUSA, and you select an Offer provided to you and proceed with acquiring a loan from the Lender, then LendingUSA and/or Lender will request a full credit report from one or more of the credit reporting agencies. This request will appear as a hard inquiry on your credit report and may affect your credit score. You further acknowledge and understand that additional soft credit inquiry reports may be requested and obtained from one or more of the credit reporting agencies during the application process or throughout the term of your loan. These soft pull inquiries will be requested: (1) to confirm that you continue to meet the Lender&apos;s credit criteria; (2) to prevent potential fraudulent activities; (3) for internal modeling and analysis purposes; (4) in servicing or monitoring any product(s) and/or service(s) you may have attained through LendingUSA and/or its Lender; (5) in collecting, enforcing or selling any loan that you may receive from Lender; (6) in providing your credit data to you; (7) in offering you financial products and services; and/or (8) for any other lawful purpose.
                            <br /><br />
                            You authorize your wireless carrier to disclose information about your account, such as subscriber status, payment method and device details, if available, to support identity verification, fraud avoidance and other uses in support of credit transactions. Information may also be shared with other service providers to further support identity verification and fraud prevention.
                            <br /><br />
                            You further authorize and expressly consent to be contacted by Lender, LendingUSA, their agents, representatives, assignees, affiliated third parties, or anyone calling on their behalf for any and all purposes (collectively “Authorized Callers”). This express consent applies to each such telephone number that you provide to any Authorized Caller, either now or in the future, even if the number is on a federal or state do-not-call list. You confirm you are the owner of the phone number(s), or that the owner authorized you to provide this phone number(s), or that you are the regular user of this phone number(s). You are providing express written consent authorizing any Authorized Caller to contact you at this number for any lawful purposes with a live operator, automatic telephone dialing system (auto-dialer) for both phone calls and text/SMS messages, prerecorded and artificial voice message, text/SMS message or other means. Authorization may be revoked as described in our Terms of Use. These telephone calls and text messages may incur message, data and access fees from your telephone provider.
                            <br /><br />
                            Electronic Signature Agreement (&quot;Agreement&quot;). By clicking the &quot;Check My Rate&quot; button, you are consenting to this Agreement and signing your Application electronically. You agree your electronic signature is the legal equivalent of your manual signature on this Application. By clicking &quot;Check My Rate&quot; you consent to be legally bound by LendingUSA&apos;s terms and conditions. You further agree that your use of a keypad, mouse, touch pad or other device to select an item, button, icon or similar act/action online, through LendingUSA&apos;s Text-to-Apply mobile platform, merchant portal, or any other LendingUSA affiliated online platform constitutes your signature (hereafter referred to as &apos;E-Signature&apos;). You also agree that no third-party verification is necessary to validate your E-Signature, and that the lack of such third-party verification will not in any way affect the enforceability of your E-Signature, or of any resulting agreement between you and LendingUSA.
                            <br /><br />
                            By clicking on &quot;Check My Rate&quot; and continuing with this Application, you certify that: 1) you are the Applicant; 2) you are over the age of 18 years; 3) you are residing in the United States and; 4) all information that has been provided by you on this Application is true and accurate.
                          </PopoverCheckbox>
                        </Column>
                        <Column className="small-12 medium-9 padded-bottom">
                          { false &&
                            <PopoverCheckbox
                              name="consentToReceiveMessages"
                              onChange={handleCheckboxChange.bind(null, 'consentToReceiveMessages')}
                              isChecked={values.consentToReceiveMessages}
                              id="consentToReceiveMessages"
                              errorMessage={errors.consentToReceiveMessages}
                              hasError={!!errors.consentToReceiveMessages}
                              label={['Request for Credit']}
                              title="Request for Credit"
                              hasScrollCondition={false}
                            >
                              The Applicant/Additional Applicant hereby acknowledges and recognizes that this is an application for credit. By submitting this application, I (We) have verified that all information submitted on this application is true and accurate to the best of our knowledge, as well as allowing LendingUSA.com, and/or its Lender(s) and other 3rd Parties to verify the enclosed information, including, but not limited to, obtaining our credit reports, contacting our employers to verify employment and income, and/or contacting our Provider/Merchant to verify the type of procedure(s) and/or service(s) and/or product(s), procedure and/or service date, deposit amount, procedure and/or service and/or product amount, and remit payment upon approval. You authorize LendingUSA.com, and/or its Lender(s) and other 3rd Parties to verify the enclosed information and to obtain consumer reports each time you request a loan, during the processing or closing of a loan to you, or at various times during the term of your loan in connection with the servicing, monitoring, collection or enforcement of the loan or the resale or potential resale of the loan. I (We) understand and agree that the Lender(s) [as defined in the Promissory Note or communicated to me] can furnish information concerning my/our account to consumer reporting agencies and others who may properly receive that information. By providing a telephone number for a cellular phone or other wireless device, I (We) are expressly consenting to receiving communications at that number, including, but not limited to, prerecorded voice message calls, text messages, and calls made by any representatives from LendingUSA.com and/or its Lender(s) and other 3rd Parties. This express consent applies to each such telephone number that I (We) provide to LendingUSA.com and/or its Lender(s) now or in the future and permits such calls regardless of their purpose. These calls and messages may incur access fees from my/our cellular provider. I (We) understand that we may opt out of this authorization by providing written notice to the parties herein. APR’s will vary depending upon credit ratings and/or payment terms that are approved. Credit approvals are valid for a limited time only. Certain fees may apply. By signing, I (We) certify that I (We) have read, agree to, and understand the disclosures herein and I (We) agree to the terms of this application and that a Provider/Merchant staff member may apply on our behalf.<br /><br />I hereby acknowledge that I am over the age of eighteen (18) years, and that I am U.S Citizen or U.S Permanent Resident, and that all the information set forth in this credit statement is true, accurate and complete full and complete disclosure thereof.
                            </PopoverCheckbox>
                          }
                        </Column>
                      </Grid>

                      <h5>Important information about procedures for opening a new account</h5>

                      <textarea className="no-resize" rows="3" readOnly defaultValue="To help the government fight the funding of terrorism and money laundering activities, Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account. What this means for you: When you open an account, we will ask for your name, address, date of birth, and other information that will allow us to identify you. We may also ask to see your driver's license or other identifying documents." />

                      {hasError &&
                        <div className="errorsContainer">
                          {
                            errorMessages && errorMessages.map((err, index) => (<p key={index}>{err}</p>))
                          }
                        </div>
                      }

                      <Button
                        className={cn('button large green w-100', style.button, isLoading ? '' : 'arrow')}
                        onClick={handleSubmitFrom.bind(null, values)}
                        isLoading={isLoading}
                        isDisabled={!(values.consentElectronicCommunication && values.consentToTermsAndConditions)}
                      >
                        Check My Rate
                      </Button>
                      <Column className="small-12 medium-12">
                        <p className={style.description}>
                          By selecting “Check My Rate” and providing your information electronically on the application form, LendingUSA does a soft credit pull that will not impact your credit score.
                        </p>

                        <p className={style.description}>
                          By providing your phone number and clicking “Check My Rate”, you consent to receive autodialed text messages from LendingUSA. Consent is not required to obtain a loan. Message frequency varies. Message and data rates may apply. Text STOP to cancel, HELP for help. See our <a href="https://lendingusa.com/terms-of-use/" className={style.footerCta} target="_blank" rel="noopener noreferrer">Terms of Use</a> and <a href="https://lendingusa.com/privacy-policy/" className={style.footerCta} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                        </p>
                      </Column>
                    </FormGroup>
                  </Column>
                  <Sidebar bottomBoundary={2240} />
                </Grid>
              :
                <Grid className="grid-margin-x max-limited">
                  <Column className={cn('small-12 medium-12', style.alignCenter)}>
                    <ReactLoading type="spin" color="#04bc6c" height={250} width={250} />
                  </Column>
                </Grid>
            }
          </div>
        </div>
      </section>
      <Footer />
      {/* <!-- Modal --> */}
      <div className={cn('reveal', style.reveal, !isPopupVisible && style.invisible)}>
        <div className={style['card-header']}>
          <h5>Terms</h5>
        </div>
        <div>
          <p>
            By providing the telephone number above and clicking &quot;Check My Rate&quot;, you confirm you are the owner of the phone number(s), or that the owner authorized you to provide the phone number(s), or that you are the regular user of this phone number(s). You are providing express written consent via your electronic signature to receive any type of communication for any purpose, including telemarketing communications, from our lending partners, LendingUSA, their agents, representatives, affiliates, assignees or anyone calling on their behalf, at the telephone number(s) provided for any lawful purpose, even if that number is on a federal or state do-not-call list. The scope of this consent includes, but is not limited to, receiving live operator calls, calls and texts via an automatic telephone dialing system (auto-dialer), prerecorded and artificial voice messages, text/SMS messages or other means. These telephone calls and text messages may incur message, data and access fees from your telephone provider. Consent is not a condition to obtain a loan.
            You understand that you can request a paper copy of these terms or may withdraw your consent at any time by:
          </p>
          <ul>
            <li>mailing your written request to LendingUSA c/o Compliance Department at 15021 Ventura Blvd #879, Sherman Oaks, CA 91403;</li>
            <li>by e-mailing <a href="mailto:customercare@lendingusa.com">customercare@lendingusa.com</a>; or</li>
            <li>by calling LendingUSA at (800) 994-6177.</li>
          </ul>
          <p>
            You may also reply “STOP” to opt out of receiving autodialed text messages only.
          </p>
        </div>

        <button className="close-button" data-close aria-label="Close reveal" type="button" onClick={togglePopup}>
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      {/* <!-- End Modal --> */}
    </form>
  );
};

Application.propTypes = {
  className: PropTypes.string,
  validator: PropTypes.object.isRequired,
  ipAddress: PropTypes.string.isRequired,
};

export default Validator(schema)(Application);
